import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { useToast } from '@/hooks/use-toast'
import { useUpdateNote } from '@/hooks/use-api'
import type { Note } from '@/types'
import { explainError } from '@/lib/explain-error'

interface EditNoteModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  note: Note | null
  parentId: string
}

export function EditNoteModal({ open, onOpenChange, note, parentId }: EditNoteModalProps) {
  const { toast } = useToast()
  const updateMutation = useUpdateNote(parentId)

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (note && open) {
      setTitle(note.title || '')
      setBody(note.body || '')
      setIsPrivate(note.is_private || false)
      setErrors({})
    }
  }, [note, open])

  const resetForm = () => {
    setTitle('')
    setBody('')
    setIsPrivate(false)
    setErrors({})
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!body.trim()) {
      newErrors.body = 'Note body is required'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!note || !validate()) return

    try {
      await updateMutation.mutateAsync({
        id: note.id,
        data: {
          title: title.trim() || undefined,
          body: body.trim(),
          is_private: isPrivate,
        },
      })

      toast({
        title: 'Success',
        description: 'Note updated successfully.',
      })

      resetForm()
      onOpenChange(false)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to update note')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) resetForm()
        onOpenChange(isOpen)
      }}
    >
      <DialogContent className="max-w-lg" aria-describedby="edit-note-description">
        <DialogHeader>
          <DialogTitle>Edit Note</DialogTitle>
          <DialogDescription id="edit-note-description">
            Update the note details. Fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="note-title">Title</Label>
            <Input
              id="note-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Optional title"
            />
          </div>

          <div>
            <Label htmlFor="note-body">Body *</Label>
            <Textarea
              id="note-body"
              value={body}
              onChange={(e) => {
                setBody(e.target.value)
                if (errors.body)
                  setErrors((prev) => {
                    const n = { ...prev }
                    delete n.body
                    return n
                  })
              }}
              placeholder="Enter note content"
              rows={5}
              aria-required="true"
              aria-invalid={!!errors.body}
              aria-describedby={errors.body ? 'note-body-error' : undefined}
            />
            {errors.body && (
              <p id="note-body-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.body}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="note-private"
              checked={isPrivate}
              onCheckedChange={(checked) => setIsPrivate(checked === true)}
            />
            <Label htmlFor="note-private" className="cursor-pointer">
              Private note
            </Label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
